import React, { useState, useEffect } from "react";
import { DataTable, Badge, Pagination } from "@/components/ui";
import { fmt } from "@/lib/formatters";
import { Eye, Edit, ShoppingBag, Calendar, User, CreditCard, Package, RotateCcw, Truck } from "lucide-react";
import { getCustomerDisplayId } from "@/constants/globalConstants";
import { downloadFile, BASE_URL } from "@/lib/API";
import toast from "react-hot-toast";

const resolveProjectId = (sale, projects = []) => {
  const req = sale.originalRequest || sale;
  // Step 1: Find matching project from projectsLocal
  const matchedProj = Array.isArray(projects) ? projects.find(p =>
    (p._id?.toString() === req.projectId?.toString()) ||
    (p.id?.toString() === req.projectId?.toString()) ||
    (p.projectId === req.projectId) ||
    (p._id?.toString() === req.project?._id?.toString()) ||
    (p.projectId === req.project?.projectId)
  ) : null;
  // Step 2: fallback to projectDetails or project
  const proj = matchedProj || req.projectDetails || req.project;
  // Step 3: use getCustomerDisplayId
  const id = proj?.customerCustomId || req.customerCustomId || proj?.customId || getCustomerDisplayId(proj) || req.projectCustomId || req.projectId || "";
  // Skip raw 24-char MongoDB hex IDs
  if (/^[a-f0-9]{24}$/i.test(String(id))) return "";
  return id;
};

export const SalesTable = ({ sales, projects = [], onView, onEdit, onRecordReturn, onDispatch }) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setPage(1);
  }, [sales]);

  const columns = [
    {
      header: "Date",
      render: (s) => (
        <div className="flex items-center gap-1.5 text-slate-500">
          <Calendar size={13} className="text-slate-400" />
          <span className="text-[12px] font-normal whitespace-nowrap">
            {s.date ? new Date(s.date).toLocaleDateString() : '—'}
          </span>
        </div>
      )
    },
    {
      header: "SD Id",
      minWidth: '100px',
      render: (s) => {
        const idVal = s.GenericId || resolveProjectId(s, projects) || '—';
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-700 tracking-wider font-mono whitespace-nowrap">
            {idVal}
          </span>
        );
      }
    },
    {
      header: "Customer Name",
      minWidth: '140px',
      render: (s) => {
        const isMR = s.recordType === 'material_request';
        let customerDisplay = s.customer || '—';
        if (isMR && s.projectId && customerDisplay.startsWith(s.projectId)) {
          customerDisplay = customerDisplay.replace(s.projectId, '').replace(/^\s*-\s*/, '');
        }
        return (
          <span className="text-[13px] font-medium text-slate-700 uppercase leading-snug whitespace-nowrap overflow-hidden text-ellipsis" title={customerDisplay}>
            {customerDisplay}
          </span>
        );
      }
    },
    {
      header: "Contact",
      render: (s) => (
        <span className="text-[12px] text-slate-500 font-normal tracking-wide whitespace-nowrap">
          {s.customerPhone || '—'}
        </span>
      )
    },
    {
      header: "Customer Type",
      minWidth: '100px',
      render: (s) => {
        const st = s.saleType || (s.recordType === 'material_request' ? 'Project Sale' : 'Direct Sale');
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap bg-blue-50 text-blue-700 border-blue-200/60">
            {st}
          </span>
        );
      }
    },
    {
      header: "Items Sold Qty",
      render: (s) => (
        <span className="text-[12px] font-medium text-slate-700">
          {s.totalQty || (s.items || []).reduce((sum, it) => sum + (Number(it.qty) || 0), 0)}
        </span>
      )
    },
    {
      header: "Amount",
      render: (s) => (
        <span className="text-[13px] font-bold text-emerald-600">
          {fmt(Number(s.netBill || s.netPayable || s.amount || 0))}
        </span>
      )
    },
    {
      header: "Store",
      render: (s) => {
        const storeName = typeof s.store === 'object' ? (s.store?.name || s.store?.storeName) : 'Main Store';
        return (
          <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">
            {storeName || '—'}
          </span>
        );
      }
    },
    {
      header: "Action",
      className: "text-center w-24",
      render: (s) => (
        <div className="flex items-center justify-center gap-1.5">
          <button
            onClick={() => onView(s)}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-all"
            title="View Details"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => onRecordReturn && onRecordReturn(s)}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-md transition-all"
            title="Record Return"
          >
            <RotateCcw size={15} />
          </button>
        </div>
      )
    }
  ];

  const getSaleTime = (s) => {
    const src = s.originalOrder || s.originalSale || {};
    const ts = new Date(src.createdAt || s.date || s.createdAt);
    return isNaN(ts) ? 0 : ts;
  };
  const sorted = [...(sales || [])].sort((a, b) => getSaleTime(b) - getSaleTime(a));
  const currentData = sorted.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.max(Math.ceil(sorted.length / itemsPerPage), 1);

  return (
    <div className="bg-white border border-slate-100 rounded-xl overflow-hidden shadow-sm">
      <DataTable 
        columns={columns} 
        data={currentData} 
        noScroll={true}
        emptyState={
          <div className="p-12 text-center bg-slate-50 border-t border-slate-100">
            <p className="text-slate-400 font-medium text-[13px]">No sales records found</p>
          </div>
        }
      />
      
      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};
